// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useDeleteRoleMutation, useUpdateRoleMutation } from "@generated";
// Role Specific
import useListRoles from "./api";
import { Role } from "./types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<{ name?: string }>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateRole, {}] = useUpdateRoleMutation();
  const [deleteRole, {}] = useDeleteRoleMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { roles, pageMeta, isLoading, refetch } = useListRoles({
    page: {
      number: pageNumber,
      size: pageSize,
    },
    filter,
  });

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((roleId) => {
      deleteRole({
        slug: accountSlug,
        id: parseInt(roleId),
      });
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const searchByName = (name: string) => {
    setFilter(name ? { name } : {});
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchByName(searchTerm);
  };

  const columns: ColumnDef<Role>[] = [
    {
      header: "Name",
      cell(props) {
        return (
          <div
            className="roleName"
            onClick={() => {
              window.location.assign(
                `/admin/roles/${props.row.original.id}/edit`
              );
            }}
          >
            {props.row.original.name}
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return <ScopeStackSpinner />;
  } else {
    return (
      <div className="rolesTable">
        <Card
          style={{
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid var(--gray-3-stroke, #D7D7D7)",
            background: " var(--white, #FFF)",
            boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Card.Header
            style={{
              backgroundColor: "#fff",
              lineHeight: "unset",
              display: "block",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="accordianTitle" style={{ margin: "0" }}>
                Roles
              </div>
              <Button
                className="squareGreenButton"
                onClick={() => window.location.assign("/admin/roles/new")}
              >
                Add A Role
              </Button>
            </div>
            <hr></hr>
            <div
              style={{
                display: "flex",
              }}
            >
              {selectedIds.length > 0 && (
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={handleDelete}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  <FontAwesomeIcon
                    style={{ color: "black" }}
                    icon={faTrashCan}
                  />
                </Button>
              )}

              <div style={{ width: "-webkit-fill-available" }}>
                <FormFieldLabel label="Search Roles" className={undefined} />
                <Form
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch(e);
                    }
                  }}
                  onSubmit={handleSearch}
                >
                  <SearchField
                    placeholder="Search by name"
                    onChange={(e) => {
                      setPageNumber(1);
                      setSearchTerm(e.target.value);
                    }}
                    value={searchTerm}
                    onClick={() => {
                      setSearchTerm("");
                      searchByName("");
                    }}
                    fullWidth
                  />
                </Form>
              </div>
            </div>
          </Card.Header>
          <DataTable
            data={roles}
            columns={columns}
            selectable
            bordered
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationEnabled
            pageSizeEnabled
          />
        </Card>
        <ConfirmationModal
          title="Confirm Delete"
          message={`Are you sure that you want to delete ${selectedIds.length} roles?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          show={showConfirmationModal}
          onHide={handleCancel}
        />
      </div>
    );
  }
};

export default List;
